import { createContext, useContext, useReducer, useMemo, type ReactNode, type Dispatch } from 'react'

export interface UIState {
  chatOpen: boolean
  participantsOpen: boolean
  webinarUsersOpen: boolean
  filesOpen: boolean
  meetingInfoPanelOpen: boolean
  whiteboardOpen: boolean
  pollsPanelOpen: boolean
  surveysPanelOpen: boolean
  brandingPanelOpen: boolean
  videoSharePanelOpen: boolean
  documentSharePanelOpen: boolean
  transcriptionPanelOpen: boolean
  settingsOpen: boolean
  helpOpen: boolean
  aboutOpen: boolean
  shortcutsOpen: boolean
  reportOpen: boolean
  hostControlsOpen: boolean
  addVideoInputOpen: boolean
  createPollOpen: boolean
  createSurveyOpen: boolean
  pollVotingOpen: boolean
  pollResultsOpen: boolean
  backgroundEffectsOpen: boolean
  liveStreamingOpen: boolean
  engagementControlsOpen: boolean
  endWebinarDialogOpen: boolean
  adjustViewOpen: boolean
}

export type UIAction =
  | { type: 'SET'; key: keyof UIState; value: boolean }
  | { type: 'TOGGLE'; key: keyof UIState }
  | { type: 'CLOSE_ALL_PANELS' }

const initialUIState: UIState = {
  chatOpen: false,
  participantsOpen: false,
  webinarUsersOpen: false,
  filesOpen: false,
  meetingInfoPanelOpen: false,
  whiteboardOpen: false,
  pollsPanelOpen: false,
  surveysPanelOpen: false,
  brandingPanelOpen: false,
  videoSharePanelOpen: false,
  documentSharePanelOpen: false,
  transcriptionPanelOpen: false,
  settingsOpen: false,
  helpOpen: false,
  aboutOpen: false,
  shortcutsOpen: false,
  reportOpen: false,
  hostControlsOpen: false,
  addVideoInputOpen: false,
  createPollOpen: false,
  createSurveyOpen: false,
  pollVotingOpen: false,
  pollResultsOpen: false,
  backgroundEffectsOpen: false,
  liveStreamingOpen: false,
  engagementControlsOpen: false,
  endWebinarDialogOpen: false,
  adjustViewOpen: false,
}

/**
 * Side-rail panels — only one of these may be open at a time. Opening any of
 * them through SET or TOGGLE atomically closes every other one in this list,
 * so the previously open panel doesn't linger underneath. Dialogs / modals
 * (`settingsOpen`, `hostControlsOpen`, etc.) are intentionally excluded —
 * those float on top and can coexist with a side panel.
 */
const SIDE_PANEL_KEYS: ReadonlyArray<keyof UIState> = [
  'chatOpen',
  'participantsOpen',
  'webinarUsersOpen',
  'filesOpen',
  'meetingInfoPanelOpen',
  // `whiteboardOpen` is deliberately NOT here. It stopped being a side panel
  // when the whiteboard became shared room state: it now occupies the main
  // stage like a screen share, and it is the room that opens and closes it.
  //
  // While it was in this mutex set, opening chat set `whiteboardOpen: false`,
  // the room-state sync immediately set it back to true, and that re-assert ran
  // the mutex again and closed chat. The visible result was a whiteboard that
  // reloaded and a panel that never opened.
  'pollsPanelOpen',
  'surveysPanelOpen',
  'brandingPanelOpen',
  'videoSharePanelOpen',
  'documentSharePanelOpen',
  'transcriptionPanelOpen',
]

const isSidePanelKey = (key: keyof UIState): boolean =>
  SIDE_PANEL_KEYS.includes(key)

/**
 * Returns `state` with every side panel forced to `false` except for
 * `keepOpen`, which is set to `true`. Used for mutex transitions on
 * SET/TOGGLE so opening one panel atomically closes any other.
 */
const openOnlySidePanel = (state: UIState, keepOpen: keyof UIState): UIState => {
  const next = { ...state }
  for (const k of SIDE_PANEL_KEYS) next[k] = false
  next[keepOpen] = true
  return next
}

function uiReducer(state: UIState, action: UIAction): UIState {
  switch (action.type) {
    case 'SET':
      if (state[action.key] === action.value) return state
      if (action.value === true && isSidePanelKey(action.key)) {
        return openOnlySidePanel(state, action.key)
      }
      return { ...state, [action.key]: action.value }
    case 'TOGGLE': {
      const nextValue = !state[action.key]
      if (nextValue === true && isSidePanelKey(action.key)) {
        return openOnlySidePanel(state, action.key)
      }
      return { ...state, [action.key]: nextValue }
    }
    case 'CLOSE_ALL_PANELS': {
      const next = { ...state }
      for (const k of SIDE_PANEL_KEYS) next[k] = false
      return next
    }
    default:
      return state
  }
}

const UIStateCtx = createContext<UIState | null>(null)
const UIDispatchCtx = createContext<Dispatch<UIAction> | null>(null)

export function UIStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(uiReducer, initialUIState)
  return (
    <UIDispatchCtx.Provider value={dispatch}>
      <UIStateCtx.Provider value={state}>
        {children}
      </UIStateCtx.Provider>
    </UIDispatchCtx.Provider>
  )
}

export function useUIState(): UIState {
  const ctx = useContext(UIStateCtx)
  if (!ctx) throw new Error('useUIState must be used within UIStateProvider')
  return ctx
}

export function useUIDispatch(): Dispatch<UIAction> {
  const ctx = useContext(UIDispatchCtx)
  if (!ctx) throw new Error('useUIDispatch must be used within UIStateProvider')
  return ctx
}

export type UISetterFn = (v: boolean | ((prev: boolean) => boolean)) => void

export function useUISetters() {
  const dispatch = useUIDispatch()
  return useMemo(() => {
    const make = (key: keyof UIState): UISetterFn =>
      (v) => {
        if (typeof v === 'function') {
          dispatch({ type: 'TOGGLE', key })
        } else {
          dispatch({ type: 'SET', key, value: v })
        }
      }
    return {
      setChatOpen: make('chatOpen'),
      setParticipantsOpen: make('participantsOpen'),
      setWebinarUsersOpen: make('webinarUsersOpen'),
      setFilesOpen: make('filesOpen'),
      setMeetingInfoPanelOpen: make('meetingInfoPanelOpen'),
      setWhiteboardOpen: make('whiteboardOpen'),
      setPollsPanelOpen: make('pollsPanelOpen'),
      setSurveysPanelOpen: make('surveysPanelOpen'),
      setBrandingPanelOpen: make('brandingPanelOpen'),
      setVideoSharePanelOpen: make('videoSharePanelOpen'),
      setDocumentSharePanelOpen: make('documentSharePanelOpen'),
      setTranscriptionPanelOpen: make('transcriptionPanelOpen'),
      setSettingsOpen: make('settingsOpen'),
      setHelpOpen: make('helpOpen'),
      setAboutOpen: make('aboutOpen'),
      setShortcutsOpen: make('shortcutsOpen'),
      setReportOpen: make('reportOpen'),
      setHostControlsOpen: make('hostControlsOpen'),
      setAddVideoInputOpen: make('addVideoInputOpen'),
      setCreatePollOpen: make('createPollOpen'),
      setCreateSurveyOpen: make('createSurveyOpen'),
      setPollVotingOpen: make('pollVotingOpen'),
      setPollResultsOpen: make('pollResultsOpen'),
      setBackgroundEffectsOpen: make('backgroundEffectsOpen'),
      setLiveStreamingOpen: make('liveStreamingOpen'),
      setEngagementControlsOpen: make('engagementControlsOpen'),
      setEndWebinarDialogOpen: make('endWebinarDialogOpen'),
      setAdjustViewOpen: make('adjustViewOpen'),
      closeAllPanels: () => dispatch({ type: 'CLOSE_ALL_PANELS' }),
    }
  }, [dispatch])
}
